
import React, { useCallback, useEffect, useState } from "react";
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Edge,
  Node,
  Connection,
  MarkerType,
  Panel,
  Controls,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Module } from "./ModuleList";
import { EnhancedModule } from "@/data/modules";
import { calculateSavingsMetrics } from "@/utils/savingsCalculations";

const moduleColors = [
  { bg: '#D3E4FD', border: '#0EA5E9', text: '#0369A1' }, // Blue
  { bg: '#F2FCE2', border: '#84CC16', text: '#3F6212' }, // Green
  { bg: '#FEF7CD', border: '#EAB308', text: '#854D0E' }, // Yellow
  { bg: '#FDE1D3', border: '#F97316', text: '#9A3412' }, // Orange
  { bg: '#FFDEE2', border: '#E11D48', text: '#9F1239' }, // Red
  { bg: '#E5DEFF', border: '#8B5CF6', text: '#6D28D9' }, // Purple
  { bg: '#F1F0FB', border: '#64748B', text: '#334155' }, // Gray
];

interface WorkflowDiagramProps {
  modules: Module[];
}

const WorkflowDiagram = ({ modules }: WorkflowDiagramProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [fitViewOnInit, setFitViewOnInit] = useState(true);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  useEffect(() => {

    const activeModules = modules.filter(module => 
      module.subModules.some(sm => sm.active)
    );
    
    if (activeModules.length === 0) {
      setNodes([{
        id: 'placeholder',
        type: 'default',
        data: { label: 'No active modules selected' },
        position: { x: 250, y: 100 },
        style: { 
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          color: '#1F2937',
          borderRadius: '12px',
          padding: '10px 15px',
          fontSize: '14px',
          width: 200,
          textAlign: 'center' as const
        }
      }]);
      setEdges([]);
      return;
    }
    
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    
    const moduleNodes: string[] = [];
    const subModuleNodes: Record<string, string[]> = {};
    const stepNodes: Record<string, { traditional: string[], withAi: string[] }> = {};
    
    // Horizontal layout positioning constants - optimized for left-to-right flow
    const MODULE_HORIZONTAL_SPACING = 600; // Increased from 400 to spread modules out more horizontally
    const MODULE_VERTICAL_SPACING = 550; // Increased for steps
    const SUBMODULE_VERTICAL_SPACING = 90; // Increased from 80 for more vertical space
    const SUBMODULE_HORIZONTAL_OFFSET = 70; // Increased from 50 to give more horizontal separation
    const STEP_VERTICAL_SPACING = 45; // Increased from 40 for more vertical spacing
    const STEP_HORIZONTAL_OFFSET = 120; // Increased from 80 to spread steps horizontally
    const MAX_MODULES_PER_ROW = 3; // Increased from 2 to utilize more horizontal space
    
    activeModules.forEach((module, moduleIndex) => {
      const moduleId = `module-${module.id}`;
      moduleNodes.push(moduleId);
      
      const colorIndex = moduleIndex % moduleColors.length;
      const moduleColor = moduleColors[colorIndex];
      
      // Calculate row and column for module
      const row = Math.floor(moduleIndex / MAX_MODULES_PER_ROW);
      const col = moduleIndex % MAX_MODULES_PER_ROW;
      
      // Add module node
      newNodes.push({
        id: moduleId,
        type: 'default',
        data: { label: module.name },
        position: { 
          x: 80 + col * MODULE_HORIZONTAL_SPACING, 
          y: 80 + row * MODULE_VERTICAL_SPACING 
        },
        style: { 
          background: '#FFFFFF',
          border: `2px solid ${moduleColor.border}`,
          color: moduleColor.text,
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '16px',
          width: 200,
          textAlign: 'center' as const,
          fontWeight: 'medium' as const
        }
      });
      
      const activeSubModules = module.subModules.filter(sm => sm.active);
      subModuleNodes[moduleId] = [];
      stepNodes[moduleId] = { traditional: [], withAi: [] };
      
      activeSubModules.forEach((subModule, subIndex) => {
        const subModuleId = `submodule-${module.id}-${subModule.id}`;
        subModuleNodes[moduleId].push(subModuleId);
        
        // Add submodule node
        newNodes.push({
          id: subModuleId,
          type: 'default',
          data: { label: subModule.name },
          position: { 
            x: 100 + SUBMODULE_HORIZONTAL_OFFSET + col * MODULE_HORIZONTAL_SPACING, 
            y: 180 + row * MODULE_VERTICAL_SPACING + subIndex * SUBMODULE_VERTICAL_SPACING 
          },
          style: { 
            background: moduleColor.bg,
            border: `1px solid ${moduleColor.border}`,
            color: moduleColor.text,
            borderRadius: '12px',
            padding: '8px 12px',
            fontSize: '13px',
            width: 180,
            textAlign: 'center' as const
          }
        });
        
        // Connect module to submodule
        newEdges.push({
          id: `edge-${moduleId}-${subModuleId}`,
          source: moduleId,
          target: subModuleId,
          style: { 
            stroke: moduleColor.border, 
            strokeWidth: 2.5,
            strokeDasharray: '5,5'
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 16,
            height: 16,
            color: moduleColor.border,
          }
        });

        // Check if this submodule has steps and steps_with_ai
        const enhancedSubModule = (subModule as any);
        if (enhancedSubModule.steps && enhancedSubModule.steps_with_ai) {
          // Add a header for traditional steps
          const traditionalHeaderId = `traditional-header-${module.id}-${subModule.id}`;
          newNodes.push({
            id: traditionalHeaderId,
            type: 'default',
            data: { label: `${subModule.name} - Traditional` },
            position: { 
              x: 80 - 70 + col * MODULE_HORIZONTAL_SPACING, 
              y: 250 + row * MODULE_VERTICAL_SPACING + subIndex * SUBMODULE_VERTICAL_SPACING 
            },
            style: { 
              background: '#F1F5F9',
              border: `1px solid #CBD5E1`,
              color: '#475569',
              borderRadius: '8px',
              padding: '5px 10px',
              fontSize: '12px',
              width: 160, // Increased from 140
              textAlign: 'center' as const,
              fontWeight: 'bold' as const
            }
          });

          // Add traditional steps
          enhancedSubModule.steps.forEach((step: string, stepIndex: number) => {
            const stepId = `step-trad-${module.id}-${subModule.id}-${stepIndex}`;
            stepNodes[moduleId].traditional.push(stepId);
            
            newNodes.push({
              id: stepId,
              type: 'default',
              data: { label: step },
              position: { 
                x: 80 - 70 + col * MODULE_HORIZONTAL_SPACING, 
                y: 290 + row * MODULE_VERTICAL_SPACING + subIndex * SUBMODULE_VERTICAL_SPACING + stepIndex * STEP_VERTICAL_SPACING 
              },
              style: { 
                background: '#FFFFFF',
                border: `1px solid #CBD5E1`,
                color: '#475569',
                borderRadius: '8px',
                padding: '6px 10px',
                fontSize: '11px',
                width: 160, // Increased from 140
                textAlign: 'left' as const
              }
            });
            
            // Connect first step to submodule or previous step
            if (stepIndex === 0) {
              newEdges.push({
                id: `edge-${subModuleId}-${stepId}`,
                source: subModuleId,
                target: stepId,
                style: { 
                  stroke: '#CBD5E1', 
                  strokeWidth: 1.5,
                  strokeDasharray: '3,3'
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 12,
                  height: 12,
                  color: '#CBD5E1',
                }
              });
            } else {
              const prevStepId = `step-trad-${module.id}-${subModule.id}-${stepIndex - 1}`;
              newEdges.push({
                id: `edge-${prevStepId}-${stepId}`,
                source: prevStepId,
                target: stepId,
                style: { 
                  stroke: '#CBD5E1', 
                  strokeWidth: 1.5,
                  strokeDasharray: '3,3'
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 12,
                  height: 12,
                  color: '#CBD5E1',
                }
              });
            }
          });
          
          // Add a header for AI steps
          const aiHeaderId = `ai-header-${module.id}-${subModule.id}`;
          newNodes.push({
            id: aiHeaderId,
            type: 'default',
            data: { label: `${subModule.name} - AI-Enhanced` },
            position: { 
              x: 320 + STEP_HORIZONTAL_OFFSET + col * MODULE_HORIZONTAL_SPACING, 
              y: 250 + row * MODULE_VERTICAL_SPACING + subIndex * SUBMODULE_VERTICAL_SPACING 
            },
            style: { 
              background: '#F0FDF4',
              border: `1px solid #86EFAC`,
              color: '#166534',
              borderRadius: '8px',
              padding: '5px 10px',
              fontSize: '12px',
              width: 160, // Increased from 140
              textAlign: 'center' as const,
              fontWeight: 'bold' as const
            }
          });

          // Add AI steps
          enhancedSubModule.steps_with_ai.forEach((step: string, stepIndex: number) => {
            const stepId = `step-ai-${module.id}-${subModule.id}-${stepIndex}`;
            stepNodes[moduleId].withAi.push(stepId);
            
            newNodes.push({
              id: stepId,
              type: 'default',
              data: { label: step },
              position: { 
                x: 320 + STEP_HORIZONTAL_OFFSET + col * MODULE_HORIZONTAL_SPACING, 
                y: 290 + row * MODULE_VERTICAL_SPACING + subIndex * SUBMODULE_VERTICAL_SPACING + stepIndex * STEP_VERTICAL_SPACING 
              },
              style: { 
                background: '#FFFFFF',
                border: `1px solid #86EFAC`,
                color: '#166534',
                borderRadius: '8px',
                padding: '6px 10px',
                fontSize: '11px',
                width: 160, // Increased from 140
                textAlign: 'left' as const
              }
            });
            
            // Connect first step to submodule or previous step
            if (stepIndex === 0) {
              newEdges.push({
                id: `edge-${subModuleId}-${stepId}`,
                source: subModuleId,
                target: stepId,
                style: { 
                  stroke: '#86EFAC', 
                  strokeWidth: 1.5,
                  strokeDasharray: '3,3'
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 12,
                  height: 12,
                  color: '#86EFAC',
                }
              });
            } else {
              const prevStepId = `step-ai-${module.id}-${subModule.id}-${stepIndex - 1}`;
              newEdges.push({
                id: `edge-${prevStepId}-${stepId}`,
                source: prevStepId,
                target: stepId,
                style: { 
                  stroke: '#86EFAC', 
                  strokeWidth: 1.5,
                  strokeDasharray: '3,3'
                },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 12,
                  height: 12,
                  color: '#86EFAC',
                }
              });
            }
          });
        }
      });
    });
    
    // Create horizontal connections between modules
    for (let i = 0; i < moduleNodes.length - 1; i++) {
      const sourceColorIndex = i % moduleColors.length;
      
      // If the next module is on the same row, connect horizontally
      const currentRow = Math.floor(i / MAX_MODULES_PER_ROW);
      const nextRow = Math.floor((i + 1) / MAX_MODULES_PER_ROW);
      
      if (currentRow === nextRow) {
        // Same row - horizontal connection
        newEdges.push({
          id: `edge-${moduleNodes[i]}-${moduleNodes[i+1]}`,
          source: moduleNodes[i],
          target: moduleNodes[i+1],
          style: { 
            stroke: moduleColors[sourceColorIndex].border, 
            strokeWidth: 3,
            strokeDasharray: '8,4'
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
            color: moduleColors[sourceColorIndex].border,
          },
          type: 'smoothstep'
        });
      } else {
        // Different rows - connect to first node in next row
        newEdges.push({
          id: `edge-${moduleNodes[i]}-${moduleNodes[i+1]}`,
          source: moduleNodes[i],
          target: moduleNodes[i+1],
          style: { 
            stroke: moduleColors[sourceColorIndex].border, 
            strokeWidth: 3,
            strokeDasharray: '8,4'
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
            color: moduleColors[sourceColorIndex].border,
          },
          type: 'smoothstep'
        });
      }
    }
    
    setNodes(newNodes);
    setEdges(newEdges);
    setFitViewOnInit(true);
  }, [modules, setNodes, setEdges]);

  const onNodesChangeHandler = (changes: any) => {
    onNodesChange(changes);
    // Reset fitViewOnInit after nodes have been changed
    if (fitViewOnInit) {
      setTimeout(() => setFitViewOnInit(false), 100);
    }
  };


  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow" style={{ minHeight: '850px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChangeHandler}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          fitViewOptions={{
            padding: 0.15, // Reduced padding to use more canvas space
            includeHiddenNodes: true,
            maxZoom: 5, // Increased max zoom for better view
          }}
          zoomOnScroll={true}
          zoomOnPinch={true}
          zoomOnDoubleClick={false}
          panOnScroll={false}
          panOnDrag={true}
          attributionPosition="bottom-right"
          className="bg-white/50 rounded-lg shadow-sm"
          defaultEdgeOptions={{
            style: { strokeWidth: 2.5, strokeDasharray: '5,5' },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 16,
              height: 16
            }
          }}
        >
          <Background color="#f3f4f6" gap={16} />
          <Controls />
          {/*<Panel position="top-right" className="bg-white/80 p-2 rounded shadow-sm border border-gray-200">*/}
          {/*  <button */}
          {/*    onClick={() => setFitViewOnInit(true)}*/}
          {/*    className="text-xs text-gray-600 px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-50"*/}
          {/*  >*/}
          {/*    Fit View*/}
          {/*  </button>*/}
          {/*</Panel>*/}
        </ReactFlow>
      </div>
      

    </div>
  );
};

export default WorkflowDiagram;
